/**
 * One matcher-v2 recommendation or check-shelf job: proved fit facts (tier,
 * concerns, unverified-job-fact notes, evidence quotes) and the three
 * feedback actions (PIVOT-301 Interface/Scope, plan §1.7/§1.9). Sits
 * alongside `MatchCard` (the legacy scorer's card) rather than replacing
 * it — see `pages/Dashboard.tsx` for how the two surfaces coexist.
 *
 * Owns its own three write endpoints end to end (frontend/CLAUDE.md §2, the
 * `CvUpload`/`ConsentGate` pattern): `answerShelfQuestion` for would-apply
 * (a preference fact, `FeedbackOrderRule`'s own input), `answerCheck` for
 * considered-before (a discovery fact, kept out of rank by contract —
 * plan §1.9: "Keep considered-before out of rank"), and `answerMissingFact`
 * for one open check-shelf question (PIVOT-371: candidate EVIDENCE, which
 * changes the job's decision). Three questions, three contracts, three
 * stores — a stored novelty answer is never read back as a skill fact.
 * The one thing that bubbles up to the caller is `onStale`: a
 * `409 check_changed` on any of the three means the evidence this card was
 * built from has moved on, and only the page holding the whole decisions
 * list can re-fetch it — which is also how an answered missing fact reaches
 * the screen, since its replacement decision may move the job between lists.
 *
 * Every answered state is INITIALIZED from the server's own facts
 * (`would_apply`, `considered_before`, each question's `answered`), never
 * only from what this component last posted: a page reload has to show the
 * same three answers back (PIVOT-371 A5), and browser state does not
 * survive one.
 *
 * No opaque total score anywhere on this card (plan §1.8) — every visible
 * fact traces to a stored reason code or a verbatim quote.
 *
 * **PIVOT-373: the card names the job, and it explains itself from stored
 * facts.** Three additions, all read straight off `facts`:
 *
 * 1. A header (A1): title, the real employer (`facts.job.employer`, which is
 *    PIVOT-368's evidenced *client* — never the board's org string), the
 *    recruiter when the posting named one, location, contract, and a link to
 *    the live posting. A recommendation the candidate cannot identify or
 *    open is not a usable result (constitution §I).
 * 2. Two labels side by side, never merged: the fit tier, and the career
 *    label (`facts.career_label`, derived in code by the backend from the
 *    two stored dimensions — PIVOT-372). The sector's own resolution stays
 *    visible when the label rests on an unverified one.
 * 3. The explanation (A2): one supported transfer (`supported_transfers` —
 *    the candidate's approved fact and the job demand it covers) plus every
 *    blocking (`missing_demands`) and nonblocking (`nonblocking_gaps`) gap,
 *    each rendered through a LOCALIZED TEMPLATE with `{{name}}`
 *    interpolation. There is no explanation model call, here or on the
 *    server: the same API facts must produce the same sentences in fr, nl
 *    and en (A2), which a generated paragraph could never guarantee.
 *
 * The demand and candidate strings interpolated into those templates are the
 * posting's and the person's OWN words and are never translated; only the
 * frame around them is.
 */

import { useState } from 'react'
import { answerCheck, answerMissingFact, answerShelfQuestion } from '../api/matches'
import { ApiError, isAbortError } from '../api/client'
import { useI18n } from '../i18n'
import { reasonCodeCategory, reasonCodeKey } from '../lib/reasonCodes'
import type { CareerLabel, MatchFitFacts } from '../types/domain'
import type { TranslationKey } from '../i18n/types'
import './MatcherV2Card.css'

/** One key per `CareerLabel`. A literal map, so every key is statically
 * checkable (`i18n` keys are never built at runtime) and a new label on the
 * wire is a type error here rather than a silently missing sentence. */
const CAREER_LABEL_KEY: Record<CareerLabel, TranslationKey> = {
  direct: 'matcherV2.careerDirect',
  sector_pivot: 'matcherV2.careerSectorPivot',
  occupation_pivot: 'matcherV2.careerOccupationPivot',
  occupation_and_sector_pivot: 'matcherV2.careerOccupationAndSectorPivot',
  familiar_occupation: 'matcherV2.careerFamiliarOccupation',
  unresolved: 'matcherV2.careerUnresolved',
}

type WouldApplyAnswer = 'yes' | 'no'
type CheckAnswerValue = 'yes' | 'no' | 'unsure'

interface MatcherV2CardProps {
  facts: MatchFitFacts
  /** The candidate-evidence identity `GET /matches/decisions` was read at
   * (PIVOT-371). Echoed back, unedited, on every missing-fact answer: the
   * server compares it, and a mismatch is the same 409 `check_changed` a
   * stale `fit_assessment_id` gives. */
  candidateEvidenceSha: string | null
  /** The card's own `fit_assessment_id` went stale mid-answer (409
   * `check_changed`) — the caller re-fetches `/matches/decisions` so the
   * next render sees current facts (Acceptance: "The API and frontend
   * agree on non-happy pending and stale-check responses."). */
  onStale: (jobId: string) => void
  /** One missing-fact answer landed and replaced this job's decision
   * (PIVOT-371 A1). The card cannot re-render the new decision by itself —
   * the job may have left this list entirely — so the page re-fetches.
   * Defaults to `onStale`'s own behaviour at the call site. */
  onAnswered?: (jobId: string) => void
}

export function MatcherV2Card({
  facts,
  candidateEvidenceSha,
  onStale,
  onAnswered,
}: MatcherV2CardProps) {
  const { t } = useI18n()
  const [wouldApplyPending, setWouldApplyPending] = useState<ReadonlySet<WouldApplyAnswer>>(
    new Set(),
  )
  // Seeded from the server's own stored answer, so a reload shows it back
  // (PIVOT-371 A5). Local state after that is only this session's optimism.
  const [wouldApplyAnswer, setWouldApplyAnswer] = useState<WouldApplyAnswer | null>(
    facts.would_apply,
  )
  const [wouldApplyFailed, setWouldApplyFailed] = useState(false)
  const [checkPending, setCheckPending] = useState<ReadonlySet<CheckAnswerValue>>(new Set())
  const [checkAnswer, setCheckAnswer] = useState<CheckAnswerValue | null>(facts.considered_before)
  const [checkFailed, setCheckFailed] = useState(false)
  const [missingFactPending, setMissingFactPending] = useState<ReadonlySet<string>>(new Set())
  // Keyed by `missing_fact_id`, and seeded from each question's own stored
  // `answered`. `'unsure'` never comes back from the server -- it stores no
  // fact -- so it lives here for this session only, which is exactly what it
  // means: the question is still open.
  const [missingFactAnswers, setMissingFactAnswers] = useState<
    Readonly<Record<string, CheckAnswerValue>>
  >(() =>
    Object.fromEntries(
      facts.missing_fact_questions
        .filter((question) => question.answered !== null)
        .map((question) => [question.missing_fact_id, question.answered as CheckAnswerValue]),
    ),
  )
  const [missingFactFailed, setMissingFactFailed] = useState(false)
  const [reassessing, setReassessing] = useState(false)

  const concerns = facts.concerns.filter((code) => reasonCodeCategory(code) === 'concern')
  const notes = facts.concerns.filter((code) => reasonCodeCategory(code) === 'note')

  const handleWouldApply = (answer: WouldApplyAnswer) => {
    setWouldApplyFailed(false)
    setWouldApplyPending((prev) => new Set(prev).add(answer))
    void answerShelfQuestion(facts.job_id, {
      fit_assessment_id: facts.fit_assessment_id,
      action: answer === 'yes' ? 'interested' : 'dismissed',
    })
      .then(() => setWouldApplyAnswer(answer))
      .catch((reason: unknown) => {
        if (isAbortError(reason)) return
        if (reason instanceof ApiError && reason.status === 409) {
          onStale(facts.job_id)
          return
        }
        setWouldApplyFailed(true)
      })
      .finally(() => {
        setWouldApplyPending((prev) => {
          const next = new Set(prev)
          next.delete(answer)
          return next
        })
      })
  }

  const handleCheck = (answer: CheckAnswerValue) => {
    setCheckFailed(false)
    setCheckPending((prev) => new Set(prev).add(answer))
    void answerCheck(facts.job_id, {
      fit_assessment_id: facts.fit_assessment_id,
      answer,
    })
      .then(() => setCheckAnswer(answer))
      .catch((reason: unknown) => {
        if (isAbortError(reason)) return
        if (reason instanceof ApiError && reason.status === 409) {
          onStale(facts.job_id)
          return
        }
        setCheckFailed(true)
      })
      .finally(() => {
        setCheckPending((prev) => {
          const next = new Set(prev)
          next.delete(answer)
          return next
        })
      })
  }

  const handleMissingFact = (missingFactId: string, answer: CheckAnswerValue) => {
    setMissingFactFailed(false)
    setMissingFactPending((prev) => new Set(prev).add(`${missingFactId}:${answer}`))
    void answerMissingFact(facts.job_id, {
      fit_assessment_id: facts.fit_assessment_id,
      candidate_evidence_sha: candidateEvidenceSha,
      missing_fact_id: missingFactId,
      answer,
    })
      .then((result) => {
        setMissingFactAnswers((prev) => ({ ...prev, [missingFactId]: answer }))
        setReassessing(result.reassessment_queued)
        // A yes/no replaced the decision, so this job may belong on the other
        // list now. Only the page holding both lists can show that.
        if (answer !== 'unsure') (onAnswered ?? onStale)(facts.job_id)
      })
      .catch((reason: unknown) => {
        if (isAbortError(reason)) return
        if (reason instanceof ApiError && reason.status === 409) {
          onStale(facts.job_id)
          return
        }
        setMissingFactFailed(true)
      })
      .finally(() => {
        setMissingFactPending((prev) => {
          const next = new Set(prev)
          next.delete(`${missingFactId}:${answer}`)
          return next
        })
      })
  }

  // A2: the explanation is assembled from stored facts through localized
  // templates -- no model call, and the same facts give the same sentences
  // in every locale. The first named transfer is the one the card leads
  // with; a transfer whose candidate half is missing (a row stored before
  // PIVOT-369) names nothing and is skipped rather than half-shown.
  const transfer = facts.supported_transfers.find((entry) => entry.candidate_fact !== null)

  return (
    <article className={`matcher-v2-card matcher-v2-card--${facts.fit_tier}`}>
      {/* A1: the job itself. The title is the link out -- the minimum usable
          action on any card is being able to open the posting. */}
      <div className="matcher-v2-card-job">
        <h3 className="matcher-v2-card-title">
          <a href={facts.job.url} target="_blank" rel="noreferrer noopener">
            {facts.job.title}
          </a>
        </h3>
        {/* The employer is PIVOT-368's evidenced CLIENT. When the posting
            never named one we say so and name the agency instead -- the
            board's own org string is the agency on a recruiter advert, and
            printing it here would tell the candidate they work for it. */}
        <p className="matcher-v2-card-employer">
          {facts.job.employer ?? t('matcherV2.employerUnverified')}
        </p>
        {facts.job.recruiter !== null && (
          <p className="matcher-v2-card-recruiter">
            {t('matcherV2.recruiterLine', { recruiter: facts.job.recruiter })}
          </p>
        )}
        <p className="matcher-v2-card-place">
          {facts.job.location ?? t('dashboard.locationNotStated')}
        </p>
        {facts.job.contract !== null && (
          <p className="matcher-v2-card-contract">
            {t('matcherV2.contractLine', { contract: facts.job.contract })}
          </p>
        )}
        <a
          className="matcher-v2-card-posting-link"
          href={facts.job.url}
          target="_blank"
          rel="noreferrer noopener"
        >
          {t('matcherV2.applyLink')}
        </a>
      </div>

      {/* Two labels, never one: how well the person fits, and how far the
          job is from the work they have done. Merging them would let a
          career distance read as a weaker fit (PIVOT-372). */}
      <span className="matcher-v2-card-tier">
        {facts.fit_tier === 'strong' && t('matcherV2.tierStrong')}
        {facts.fit_tier === 'possible' && t('matcherV2.tierPossible')}
        {facts.fit_tier === 'stretch' && t('matcherV2.tierStretch')}
        {facts.fit_tier === 'not_recommended' && t('matcherV2.tierUnresolved')}
      </span>

      <div className="matcher-v2-card-section">
        <h4 className="matcher-v2-card-heading">{t('matcherV2.careerHeading')}</h4>
        {/* Rendered from the label the server derived; this component never
            re-derives one from the two dimensions. */}
        <p className="matcher-v2-card-career">{t(CAREER_LABEL_KEY[facts.career_label])}</p>
        {facts.career_sector_resolution === 'unverified' && (
          <p className="matcher-v2-card-note">{t('matcherV2.careerSectorUnverified')}</p>
        )}
      </div>

      {(transfer !== undefined ||
        facts.missing_demands.length > 0 ||
        facts.nonblocking_gaps.length > 0) && (
        <div className="matcher-v2-card-section">
          <h4 className="matcher-v2-card-heading">{t('matcherV2.explanationHeading')}</h4>
          <ul className="matcher-v2-card-list">
            {transfer !== undefined && (
              <li key="transfer">
                {t('matcherV2.transferLine', {
                  fact: transfer.candidate_fact ?? '',
                  demand: transfer.demand,
                })}
              </li>
            )}
            {facts.missing_demands.map((demand) => (
              <li key={`blocking-${demand}`}>{t('matcherV2.blockingGap', { demand })}</li>
            ))}
            {facts.nonblocking_gaps.map((demand) => (
              <li key={`nonblocking-${demand}`}>{t('matcherV2.nonblockingGap', { demand })}</li>
            ))}
          </ul>
        </div>
      )}

      {concerns.length > 0 && (
        <div className="matcher-v2-card-section">
          <h4 className="matcher-v2-card-heading">{t('matcherV2.concernsHeading')}</h4>
          <ul className="matcher-v2-card-list">
            {concerns.map((code) => (
              <li key={code}>{t(reasonCodeKey(code))}</li>
            ))}
          </ul>
        </div>
      )}

      {notes.length > 0 && (
        <div className="matcher-v2-card-section">
          <h4 className="matcher-v2-card-heading">{t('matcherV2.notesHeading')}</h4>
          <ul className="matcher-v2-card-list">
            {notes.map((code) => (
              <li key={code}>{t(reasonCodeKey(code))}</li>
            ))}
          </ul>
        </div>
      )}

      {facts.quotes.length > 0 && (
        <div className="matcher-v2-card-section">
          <h4 className="matcher-v2-card-heading">{t('matcherV2.quotesHeading')}</h4>
          {facts.quotes.map((evidence, index) => (
            // `quote_start`/`text_sha` disambiguate two identical quotes
            // supporting different reason codes -- an index alone would
            // silently collide.
            <blockquote
              key={`${evidence.text_sha ?? 'quote'}-${evidence.quote_start ?? index}`}
              className="matcher-v2-card-quote"
              lang={facts.canonical_text_language}
            >
              {evidence.quote}
            </blockquote>
          ))}
          <p className="matcher-v2-card-quote-note">{t('matcherV2.quoteSourceNote')}</p>
        </div>
      )}

      {/* PIVOT-371: the posting could not be read, so the only honest offer
          is the posting itself. Never a question -- the facts missing here
          are the employer's, and asking the person for them would be asking
          them to invent one (constitution SI). */}
      {facts.information_insufficient && (
        <div className="matcher-v2-card-section">
          <h4 className="matcher-v2-card-heading">{t('matcherV2.insufficientHeading')}</h4>
          <p className="matcher-v2-card-note">{t('matcherV2.insufficientBody')}</p>
          <a
            className="matcher-v2-card-posting-link"
            href={facts.posting_url}
            target="_blank"
            rel="noreferrer noopener"
          >
            {t('matcherV2.insufficientLink')}
          </a>
        </div>
      )}

      {facts.missing_fact_questions.length > 0 && (
        <div className="matcher-v2-card-feedback">
          <h4 className="matcher-v2-card-heading">{t('matcherV2.missingFactHeading')}</h4>
          {facts.missing_fact_questions.map((question) => {
            const answered = missingFactAnswers[question.missing_fact_id] ?? null
            const isPending = (value: CheckAnswerValue) =>
              missingFactPending.has(`${question.missing_fact_id}:${value}`)
            return (
              <div className="matcher-v2-card-missing-fact" key={question.missing_fact_id}>
                <p className="matcher-v2-card-question">
                  {t('matcherV2.missingFactQuestion')}{' '}
                  {/* The demand is the posting's own wording, in the
                      posting's own language -- rendered, never translated. */}
                  <span lang={facts.canonical_text_language}>{question.missing_fact_id}</span>
                </p>
                <div className="matcher-v2-card-choice-row">
                  <button
                    type="button"
                    className={`btn ${answered === 'yes' ? 'btn-primary' : 'btn-outline'}`}
                    disabled={isPending('yes')}
                    aria-busy={isPending('yes')}
                    onClick={() => handleMissingFact(question.missing_fact_id, 'yes')}
                  >
                    {t(
                      answered === 'yes'
                        ? 'matcherV2.missingFactYesMarked'
                        : 'matcherV2.missingFactYes',
                    )}
                  </button>
                  <button
                    type="button"
                    className={`btn ${answered === 'no' ? 'btn-settled' : 'btn-outline'}`}
                    disabled={isPending('no')}
                    aria-busy={isPending('no')}
                    onClick={() => handleMissingFact(question.missing_fact_id, 'no')}
                  >
                    {t(
                      answered === 'no'
                        ? 'matcherV2.missingFactNoMarked'
                        : 'matcherV2.missingFactNo',
                    )}
                  </button>
                  <button
                    type="button"
                    className={`btn ${answered === 'unsure' ? 'btn-settled' : 'btn-outline'}`}
                    disabled={isPending('unsure')}
                    aria-busy={isPending('unsure')}
                    onClick={() => handleMissingFact(question.missing_fact_id, 'unsure')}
                  >
                    {t(
                      answered === 'unsure'
                        ? 'matcherV2.missingFactUnsureMarked'
                        : 'matcherV2.missingFactUnsure',
                    )}
                  </button>
                </div>
                {answered === 'unsure' && (
                  <p className="matcher-v2-card-note">{t('matcherV2.missingFactUnsureNote')}</p>
                )}
              </div>
            )
          })}
          {reassessing && (
            <p className="matcher-v2-card-note">{t('matcherV2.missingFactReassessing')}</p>
          )}
          {missingFactFailed && <p className="form-error">{t('common.error')}</p>}
        </div>
      )}

      <div className="matcher-v2-card-feedback">
        <p className="matcher-v2-card-question">{t('matcherV2.wouldApplyQuestion')}</p>
        <div className="matcher-v2-card-choice-row">
          <button
            type="button"
            className={`btn ${wouldApplyAnswer === 'yes' ? 'btn-primary' : 'btn-outline'}`}
            disabled={wouldApplyPending.has('yes')}
            aria-busy={wouldApplyPending.has('yes')}
            onClick={() => handleWouldApply('yes')}
          >
            {t(wouldApplyAnswer === 'yes' ? 'matcherV2.wouldApplyYesMarked' : 'matcherV2.wouldApplyYes')}
          </button>
          <button
            type="button"
            className={`btn ${wouldApplyAnswer === 'no' ? 'btn-settled' : 'btn-outline'}`}
            disabled={wouldApplyPending.has('no')}
            aria-busy={wouldApplyPending.has('no')}
            onClick={() => handleWouldApply('no')}
          >
            {t(wouldApplyAnswer === 'no' ? 'matcherV2.wouldApplyNoMarked' : 'matcherV2.wouldApplyNo')}
          </button>
        </div>
        {/* Plan §1.9: "A dismissal reason stays descriptive at launch" -- a
            plain sentence explaining the effect, never a silent removal. */}
        {wouldApplyAnswer === 'no' && (
          <p className="matcher-v2-card-note">{t('matcherV2.wouldApplyNoNote')}</p>
        )}
        {wouldApplyFailed && <p className="form-error">{t('common.error')}</p>}
      </div>

      <div className="matcher-v2-card-feedback">
        <p className="matcher-v2-card-question">{t('matcherV2.checkQuestion')}</p>
        <div className="matcher-v2-card-choice-row">
          <button
            type="button"
            className={`btn ${checkAnswer === 'yes' ? 'btn-primary' : 'btn-outline'}`}
            disabled={checkPending.has('yes')}
            aria-busy={checkPending.has('yes')}
            onClick={() => handleCheck('yes')}
          >
            {t(checkAnswer === 'yes' ? 'matcherV2.checkYesMarked' : 'matcherV2.checkYes')}
          </button>
          <button
            type="button"
            className={`btn ${checkAnswer === 'no' ? 'btn-settled' : 'btn-outline'}`}
            disabled={checkPending.has('no')}
            aria-busy={checkPending.has('no')}
            onClick={() => handleCheck('no')}
          >
            {t(checkAnswer === 'no' ? 'matcherV2.checkNoMarked' : 'matcherV2.checkNo')}
          </button>
          <button
            type="button"
            className={`btn ${checkAnswer === 'unsure' ? 'btn-settled' : 'btn-outline'}`}
            disabled={checkPending.has('unsure')}
            aria-busy={checkPending.has('unsure')}
            onClick={() => handleCheck('unsure')}
          >
            {t(checkAnswer === 'unsure' ? 'matcherV2.checkUnsureMarked' : 'matcherV2.checkUnsure')}
          </button>
        </div>
        {checkFailed && <p className="form-error">{t('common.error')}</p>}
      </div>
    </article>
  )
}
